import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Group, Typography, useHeader, Wrapper } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useAllessehCollectionQuery } from 'hooks/useAllessehCollectionQuery';
import { useAllessehCollectionSummariesQuery } from 'hooks/useAllessehCollectionSummariesQuery';
import {
  AllessehQuerySortKeyTypes,
  AllessehQuerySortOrderType,
  useAllessehContentQuery
} from 'hooks/useAllessehContentQuery';
import { usePageAnalytics } from 'hooks/usePageAnalytics';
import { formatInTimeZone } from 'utils/dates';
import ArticleCard from './components/ArticleCard';
import CollectionNode from './components/CollectionNode';
import DebugPageForm from './components/DebugPageForm';
import PageAnalytics from './components/PageAnalytics';
import ParametersDisplay from './components/ParametersDisplay';
import {
  type Article,
  type CollectionOrArticle,
  extractEmbargoArticlesFromRawData,
  findEmbargoArticleById,
  findRawNode,
  resolveAllessehId,
  validatePageArticles
} from './Debug.utils';

const DebugPage = () => {
  // Get allessehId from route params if present
  const { allessehId: routeAllessehId } = useParams<{ allessehId?: string }>();
  const queryClient = useQueryClient();

  const [resolvedAllessehId, setResolvedAllessehId] = useState('');
  const [embargoArticleIds, setEmbargoArticleIds] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const currentProperty = useConvertedProperty();
  const { data: publicationSettings } = usePublicationSettings();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { containerHeight } = useHeader();

  // Update resolved Allesseh ID when input changes
  useEffect(() => {
    if (!routeAllessehId) {
      setResolvedAllessehId('');
      return;
    }
    const resolved = resolveAllessehId(
      routeAllessehId,
      publicationSettings?.publicationSetting.slugMappings ?? undefined
    );
    setResolvedAllessehId(resolved);
  }, [routeAllessehId, publicationSettings]);

  const { data: rawCollectionData, refetch: refetchRawCollection } = useAllessehCollectionQuery(resolvedAllessehId, {
    enabled: !!resolvedAllessehId && resolvedAllessehId.length > 0
  });

  // Update embargoArticleIds from rawCollectionData
  useEffect(() => {
    if (rawCollectionData?.data?.attributes.collection) {
      const embargoIds = extractEmbargoArticlesFromRawData(rawCollectionData.data.attributes.collection);
      setEmbargoArticleIds(Array.from(embargoIds));
    }
  }, [rawCollectionData]);

  // Request embargoed articles using useAllessehContentQuery
  const embargoQueryBody = (() => {
    const baseQuery = {
      query: {
        and: [
          {
            terms: {
              key: 'UpstreamOriginId',
              value: embargoArticleIds
            }
          },
          { term: { key: 'ContentStatus', value: 'embargo' } }
        ]
      },
      sort: [{ key: AllessehQuerySortKeyTypes.EmbargoDate, order: AllessehQuerySortOrderType.ASC }],
      count: embargoArticleIds.length
    };

    // Extract Product values from baseAllessehQuery if available
    if (publicationSettings?.publicationSetting.baseAllessehQuery) {
      try {
        const parsedBaseQuery = JSON.parse(publicationSettings.publicationSetting.baseAllessehQuery) as {
          query?: { and?: Array<{ term?: { key: string; value: string } }> };
        };
        const productTerms = parsedBaseQuery.query?.and?.filter((term) => term.term?.key === 'Product');
        if (productTerms && productTerms.length > 0) {
          productTerms.forEach((productTerm) => {
            if (productTerm.term) {
              baseQuery.query.and.push({ term: { key: 'Product', value: productTerm.term.value } });
            }
          });
        }
      } catch {
        // If parsing fails, continue without Product term
      }
    }

    return baseQuery;
  })();

  const { data: embargoArticlesData, isLoading: isEmbargoArticlesLoading } = useAllessehContentQuery(embargoQueryBody, {
    ignoreEmbargoCheck: false,
    enabled: embargoArticleIds.length > 0,
    publicationKey: publicationSettings?.publicationSetting.publicationKey
  });

  const {
    data: collectionSummaryData,
    isLoading: isCollectionSummaryLoading,
    error: collectionSummaryError,
    refetch: refetchCollectionSummary
  } = useAllessehCollectionSummariesQuery(resolvedAllessehId, {
    enabled: !!resolvedAllessehId && resolvedAllessehId.length > 0
  });

  // Extract collection type from metadata to support all content types (newsletters, banners, etc.)
  const collectionType = useMemo(() => {
    const meta = (rawCollectionData?.meta ?? collectionSummaryData?.meta) as Record<string, unknown> | undefined;
    return (meta?.collection_type ?? 'Curation_Section') as string;
  }, [rawCollectionData?.meta, collectionSummaryData?.meta]);

  // WORKAROUND: Some container types inherit from parent containers which serialize with different collection types
  // This causes analytics to be stored under the parent's collection type instead of their own
  const analyticsCollectionType = useMemo(() => {
    switch (collectionType) {
      case 'Curation_Newsletter':
        // NewsletterPageContainer extends PageContainer → serializes as Curation_Page
        return 'Curation_Page';
      case 'Curation_Banner':
        // BannerModuleContainer extends ModuleContainer → serializes as Curation_Module
        return 'Curation_Module';
      case 'Curation_OffPlatform':
        // OffPlatformListContainer extends ListContainer → serializes as Curation_List
        return 'Curation_List';
      case 'Curation_Collection':
        // Collection uses ListContainer → serializes as Curation_List
        return 'Curation_List';
      default:
        // Other types (Curation_Section, Curation_Page, Curation_Issue, etc.) serialize correctly
        return collectionType;
    }
  }, [collectionType]);

  const { data: pageAnalyticsData, refetch: refetchPageAnalytics } = usePageAnalytics({
    allessehId: routeAllessehId ?? '',
    publicationKey: currentProperty as string,
    collectionType: analyticsCollectionType,
    revisorUser: null,
    limit: 10
  });

  // Centralized latest published analytics entry
  const latestPublishedAnalytics = pageAnalyticsData?.getPageAnalytics.published[0] ?? null;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate all three queries to clear cached data before refetching
      await queryClient.invalidateQueries({ queryKey: ['useAllessehCollectionSummariesQuery', resolvedAllessehId] });
      await queryClient.invalidateQueries({ queryKey: ['useAllessehCollectionQuery', resolvedAllessehId] });
      await queryClient.invalidateQueries({
        queryKey: [
          'GetPageAnalytics',
          {
            allessehId: routeAllessehId ?? '',
            publicationKey: currentProperty as string,
            collectionType: analyticsCollectionType,
            revisorUser: null,
            limit: 10
          }
        ]
      });

      // Refetch all data
      await Promise.all([refetchCollectionSummary(), refetchRawCollection(), refetchPageAnalytics()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const renderCollectionHierarchy = () => {
    if (!rawCollectionData?.data?.attributes.collection || !collectionSummaryData) {
      return <Typography variant="note">No raw collection data available</Typography>;
    }

    // Calculate validation once for all articles
    const validation = validatePageArticles(
      { data: rawCollectionData.data },
      collectionSummaryData,
      embargoArticlesData
    );

    const renderFromResolvedData = (resolvedCollection: CollectionOrArticle[], level = 0): JSX.Element[] | null => {
      if (!Array.isArray(resolvedCollection)) return null;

      return resolvedCollection.map((item, index) => {
        if (item.type === 'collection') {
          const rawNode = rawCollectionData.data
            ? findRawNode(rawCollectionData.data.attributes.collection as unknown as Article[], item.id)
            : null;

          return (
            <CollectionNode
              key={`${item.id}-${index}`}
              item={item}
              index={index}
              level={level}
              rawNode={rawNode}
              collectionSummaryData={collectionSummaryData}
              renderFromResolvedData={renderFromResolvedData}
              missingArticles={validation.missingArticles}
              embargoArticlesData={embargoArticlesData}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              containerHeight={containerHeight}
            />
          );
        }

        // This is an article at the top level (shouldn't happen)
        return (
          <ArticleCard
            key={item.id}
            articleId={item.id}
            rawArticleData={item as Article}
            collectionSummaryData={collectionSummaryData}
            embargoArticleData={findEmbargoArticleById(item.id, embargoArticlesData)}
            isMissing={validation.missingArticles.has(item.id)}
          />
        );
      });
    };

    return (
      <>
        {/* Display root level parameters */}
        {'parameters' in rawCollectionData.data.attributes && (
          <ParametersDisplay
            parameters={
              (
                rawCollectionData.data.attributes as {
                  parameters: Array<{ type: string; name: string; value?: unknown }>;
                }
              ).parameters
            }
            title="Parameters"
            skipQuery={false}
          />
        )}

        {renderFromResolvedData(
          collectionSummaryData.data.attributes.collection as unknown as CollectionOrArticle[],
          0
        )}
      </>
    );
  };

  return (
    <Wrapper margin={{ top: 'lg' }}>
      <Typography variant="header2" margin={{ bottom: 'md' }}>
        Debug Page
        {collectionSummaryData?.meta.name && <>: {collectionSummaryData.meta.name}</>}
        {routeAllessehId ? ` (${routeAllessehId})` : ''}
      </Typography>
      <DebugPageForm
        initialValue={routeAllessehId ?? ''}
        hasError={(!!routeAllessehId && !resolvedAllessehId && !!publicationSettings) || !!collectionSummaryError}
        isRefreshing={isRefreshing}
        isLoading={isCollectionSummaryLoading}
        onRefresh={handleRefresh}
        resolvedAllessehId={resolvedAllessehId}
      />

      <>
        {collectionSummaryData && !(embargoArticleIds.length > 0 && isEmbargoArticlesLoading) && (
          <>
            <Box padding={{ all: 'md' }} margin={{ bottom: 'lg' }}>
              <Group direction="column" gap="sm">
                <Typography size="sm">
                  <strong>Publication:</strong>{' '}
                  {(() => {
                    const meta = (rawCollectionData?.meta ?? collectionSummaryData.meta) as Record<string, unknown>;
                    return String(meta.publication_key ?? 'Unknown');
                  })()}
                </Typography>
                <Typography size="sm">
                  <strong>Collection Type:</strong>{' '}
                  {(() => {
                    const meta = (rawCollectionData?.meta ?? collectionSummaryData.meta) as Record<string, unknown>;
                    return String(meta.collection_type ?? 'Unknown');
                  })()}
                </Typography>
                <Typography size="sm">
                  <strong>Version:</strong> Allesseh:{' '}
                  {collectionSummaryData.data.attributes.publish_version ?? 'Unknown'}
                  {latestPublishedAnalytics && (
                    <>
                      &nbsp;(Tool: {latestPublishedAnalytics.page.publishVersion})
                      {collectionSummaryData.data.attributes.publish_version !==
                      latestPublishedAnalytics.page.publishVersion
                        ? ' ⚠️'
                        : ' ✅'}
                    </>
                  )}
                </Typography>
                <Typography size="sm">
                  <strong>Last updated: </strong>
                  {(() => {
                    const tzObj = publicationSettings?.publicationSetting.preferredTimezone;
                    const updated = collectionSummaryData.data.attributes.updated_datetime_utc;
                    let formatted = 'Unknown';
                    if (updated && tzObj) {
                      try {
                        formatted = formatInTimeZone(new Date(updated), tzObj, 'MMM dd, yyyy h:mm:ss a zzz');
                      } catch {
                        formatted = updated;
                      }
                    }
                    // Also show newest analytics published entry
                    let analyticsTime = '';
                    let timeDiffIcon = null;
                    let diffSeconds: number | null = null;
                    if (latestPublishedAnalytics?.page.publishUtc && tzObj) {
                      try {
                        analyticsTime = formatInTimeZone(
                          new Date(latestPublishedAnalytics.page.publishUtc),
                          tzObj,
                          'MMM dd, yyyy h:mm:ss a zzz'
                        );
                        // Compare times
                        const rawTime = updated ? new Date(updated).getTime() : 0;
                        const analyticsTimeVal = new Date(latestPublishedAnalytics.page.publishUtc).getTime();
                        diffSeconds = Math.abs(rawTime - analyticsTimeVal) / 1000;
                        if (diffSeconds <= 60) {
                          timeDiffIcon = <span style={{ color: 'green', marginLeft: 8 }}>✅</span>;
                        } else if (diffSeconds > 60 && diffSeconds <= 120) {
                          timeDiffIcon = <span style={{ color: 'orange', marginLeft: 8 }}>⚠️</span>;
                        } else if (diffSeconds > 120) {
                          timeDiffIcon = <span style={{ color: 'red', marginLeft: 8 }}>❌</span>;
                        }
                      } catch {
                        analyticsTime = String(latestPublishedAnalytics.page.publishUtc);
                      }
                    }
                    return (
                      <>
                        Allesseh: {formatted}
                        {analyticsTime && <> (Tool: {analyticsTime})</>}
                        {timeDiffIcon}
                      </>
                    );
                  })()}
                </Typography>
                <Typography size="sm">
                  <strong>Revisor User:</strong>{' '}
                  {(() => {
                    let revisorUser = '';
                    if (collectionSummaryData.meta.revisor_user) {
                      revisorUser = String(collectionSummaryData.meta.revisor_user);
                    }
                    let analyticsRevisor = '';
                    if (latestPublishedAnalytics?.page.revisorUser) {
                      analyticsRevisor = String(latestPublishedAnalytics.page.revisorUser);
                    }
                    let userMatchIcon = null;
                    if (revisorUser && analyticsRevisor) {
                      userMatchIcon = revisorUser === analyticsRevisor ? '✅' : '❌';
                    }
                    return (
                      <>
                        Allesseh: {revisorUser || 'Unknown'}
                        {analyticsRevisor && <> (Tool: {analyticsRevisor})</>}
                        {userMatchIcon}
                      </>
                    );
                  })()}
                </Typography>

                {/* Article validation summary */}
                {rawCollectionData?.data?.attributes.collection && (
                  <>
                    {(() => {
                      const validation = validatePageArticles(
                        { data: rawCollectionData.data },
                        collectionSummaryData,
                        embargoArticlesData
                      );

                      return (
                        <Typography size="sm">
                          <strong>Articles:</strong>{' '}
                          {validation.missingArticles.size === 0 && validation.missingEmbargoArticles.size === 0 ? (
                            <Typography inline color="emerald" size="sm">
                              ✅ {validation.resolvedArticleCount} articles OK
                              {validation.embargoedArticles.size > 0 &&
                                ` (includes ${validation.embargoedArticles.size} embargo)`}
                            </Typography>
                          ) : (
                            <Typography inline color="lava" size="sm">
                              ❌ {validation.missingArticles.size + validation.missingEmbargoArticles.size} articles not
                              OK
                              {validation.missingEmbargoArticles.size > 0 &&
                                ` (includes ${validation.missingEmbargoArticles.size} embargo)`}
                              , {validation.resolvedArticleCount} articles OK
                              {validation.embargoedArticles.size > 0 &&
                                ` (includes ${validation.embargoedArticles.size} embargo)`}
                            </Typography>
                          )}
                        </Typography>
                      );
                    })()}
                  </>
                )}

                {!latestPublishedAnalytics && (
                  <Typography variant="note" size="sm">
                    <strong>Note:</strong> No tool publish data available for {resolvedAllessehId}.
                  </Typography>
                )}
              </Group>
            </Box>

            {(() => {
              const meta = (rawCollectionData?.meta ?? collectionSummaryData.meta) as Record<string, unknown>;

              const metaEntries = Object.entries(meta);

              if (metaEntries.length === 0) return null;

              return (
                <Wrapper margin={{ bottom: 'lg' }}>
                  <details>
                    <summary>
                      <Typography variant="header4" inline>
                        Additional Metadata
                      </Typography>
                    </summary>
                    <Group direction="column" gap="sm" fullWidth>
                      {metaEntries.map(([key, value]) => (
                        <Typography key={key} size="sm">
                          <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}:</strong>{' '}
                          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                        </Typography>
                      ))}
                    </Group>
                  </details>
                </Wrapper>
              );
            })()}

            <PageAnalytics
              published={pageAnalyticsData?.getPageAnalytics.published ?? []}
              preferredTimezone={publicationSettings?.publicationSetting.preferredTimezone}
            />

            {(() => {
              const debugData = (collectionSummaryData.meta as { debug?: Record<string, unknown> }).debug;
              if (!debugData) return null;

              // Filter out empty arrays and objects
              const nonEmptyEntries = Object.entries(debugData).filter(([, value]) => {
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
                return value !== null && value !== undefined && value !== '';
              });

              if (nonEmptyEntries.length === 0) return null;

              return (
                <Box margin={{ bottom: 'md' }} padding={{ all: 'md' }}>
                  <Box.Title>Allesseh Warnings</Box.Title>
                  <Box.Content>
                    {nonEmptyEntries.map(([key, value]) => (
                      <Group gap="sm" direction="column" key={key}>
                        <strong>{key}:</strong>
                        {typeof value === 'object' && value !== null ? (
                          <>
                            {Object.entries(value).map(([subKey, subValue]) => (
                              <p key={subKey}>
                                {subKey}: {subValue}
                              </p>
                            ))}
                          </>
                        ) : (
                          value
                        )}
                      </Group>
                    ))}
                  </Box.Content>
                </Box>
              );
            })()}

            {collectionSummaryData.data.attributes.collection.length > 0 && <>{renderCollectionHierarchy()}</>}
          </>
        )}

        {((isCollectionSummaryLoading && resolvedAllessehId) ||
          (embargoArticleIds.length > 0 && isEmbargoArticlesLoading)) && <ResponsiveLoader />}

        {collectionSummaryData?.errors && collectionSummaryData.errors.length > 0 && (
          <>
            <Box margin={{ bottom: 'sm' }}>
              <Box.Title>API Errors</Box.Title>
              <Box.Content>
                {collectionSummaryData.errors.map((error, index) => (
                  <Typography key={index} color="lava">
                    {JSON.stringify(error, null, 2)}
                  </Typography>
                ))}
              </Box.Content>
            </Box>
          </>
        )}
      </>
    </Wrapper>
  );
};

export default DebugPage;
